import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useScrollAnimation(options: UseScrollAnimationOptions = {}) {
  const { threshold = 0.06, rootMargin = '0px 0px -80px 0px', once = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

// ── Animation class presets ──
export const animationClasses = {
  base: 'transition-all duration-600 ease-out',
  hidden: 'opacity-0 translate-y-12',
  visible: 'opacity-100 translate-y-0',
};

export type AnimationVariant = 'fadeUp' | 'fadeDown' | 'fadeLeft' | 'fadeRight' | 'scaleIn' | 'fadeIn';

const variantMap: Record<AnimationVariant, { hidden: string; visible: string }> = {
  fadeUp:    { hidden: 'opacity-0 translate-y-12',   visible: 'opacity-100 translate-y-0' },
  fadeDown:  { hidden: 'opacity-0 -translate-y-10',  visible: 'opacity-100 translate-y-0' },
  fadeLeft:  { hidden: 'opacity-0 translate-x-10',   visible: 'opacity-100 translate-x-0' },
  fadeRight: { hidden: 'opacity-0 -translate-x-10',  visible: 'opacity-100 translate-x-0' },
  scaleIn:   { hidden: 'opacity-0 scale-95',          visible: 'opacity-100 scale-100' },
  fadeIn:    { hidden: 'opacity-0',                    visible: 'opacity-100' },
};

const baseTransition = 'transition-all duration-600 ease-out';

/**
 * Returns spreadable props for a single staggered element.
 * Usage: <div {...getStaggerProps(isVisible, index)}>...</div>
 */
export function getStaggerProps(
  isVisible: boolean,
  index: number,
  baseDelay = 100,
  variant: AnimationVariant = 'fadeUp',
) {
  const v = variantMap[variant];
  return {
    className: `${baseTransition} ${isVisible ? v.visible : v.hidden}`,
    style: { transitionDelay: `${baseDelay + index * 80}ms` },
  };
}

export function useStaggerDelay(itemsCount: number, baseDelay = 100) {
  return useCallback(
    (index: number) => `${baseDelay + index * 80}ms`,
    [baseDelay],
  );
}
